<template>
  <b-modal
    :id="MODAL_ID"
    :title="title"
    size="lg"
    centered
    ok-title="Save"
    @ok.prevent="onSave"
  >
    <b-row>
      <b-col cols="12">
        <label>Add Custom Size:</label>
        <div class="d-flex align-items-center">
          <b-input-group class="mr-1">
            <b-form-input
              ref="customSizeWidth"
              v-model.number="customSize.width"
              type="number"
              @input="(value) => onCustomSizeChange(value, CUSTOM_SIZE_INPUT_TYPES.WIDTH_INPUT)"
            />
            <b-input-group-append>
              <b-input-group-text style="border-right: none;">
                <b-icon icon="x" />
              </b-input-group-text>
            </b-input-group-append>

            <b-form-input
              v-model.number="customSize.height"
              type="number"
              @input="(value) => onCustomSizeChange(value, CUSTOM_SIZE_INPUT_TYPES.HEIGHT_INPUT)"
            />
          </b-input-group>
          <b-form-checkbox
            v-model="maintainAspectRatio"
            class="w-100"
            @change="onMaintainAspectRatioChange"
          >
            Maintain aspect ratio
          </b-form-checkbox>
        </div>
      </b-col>
    </b-row>
    <div class="my-3" />
    <template #modal-footer="{ cancel, ok }">
      <div class="w-100 d-flex justify-content-end">
        <div class="d-flex">
          <b-button
            @click="cancel"
          >
            Cancel
          </b-button>
          <div class="mx-1" />
          <b-button
            variant="primary"
            :disabled="!customSize.width || !customSize.height || isSaving"
            @click="ok"
          >
            Save
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { bulkCopyAndResizeEventBroker, EVENTS } from '@frontend/group/modules/bulk-copy-and-resize/event-broker';
import { copyAndResizeEventBroker, EVENTS as COPY_AND_RESIZE} from '@frontend/group/modules/copy-and-resize/event-broker';
import { getTypeDimension } from '@frontend/group/modules/copy-and-resize/helpers';
import { TYPE_DIMENSION } from '@frontend/group/modules/copy-and-resize/handlers/resize-template-handler';
import { ALIGN_CONTENT } from '@frontend/group/modules/copy-and-resize/helpers';
import layout from '../../../../store/layout';

const MODAL_ID = 'bulk-copy-and-resize-templates-modal';

const CUSTOM_SIZE_INPUT_TYPES = {
  WIDTH_INPUT: 'width',
  HEIGHT_INPUT: 'height',
}


export default {
  props: {
    customerId: {
      type: [Number, String],
      required: true
    },
    layoutId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      MODAL_ID,
      title: 'Bulk Copy and Resize Template',
      isLoading: false,
      isSaving: false,
      selectedTemplates: [],
      customSize: {
        width: '',
        height: ''
      },
      maintainAspectRatio: false,
      selectedSizes: [],
      CUSTOM_SIZE_INPUT_TYPES,
      isProportionalDimension: false,
      canvasSize: {
        width: 0,
        height: 0,
      },
      alignContent: {
        originX: ALIGN_CONTENT.START,
        originY: ALIGN_CONTENT.START,
      },
      isBulkResize: false,
      isBulkResizeSelected: false,
    }
  },
  computed: {
    ...mapGetters('layout',
      ['selectedInstance']
    ),
  },
  mounted() {
    bulkCopyAndResizeEventBroker.on(EVENTS.INIT, this.init.bind(this));
    bulkCopyAndResizeEventBroker.on(EVENTS.CLOSED, this.onClose.bind(this));
    
  },
  methods: {
    init(options = {}) {
      this.isBulkResize = options.isBulkResize || false;
      this.isBulkResizeSelected = options.isBulkResizeSelected || false;
      this.title = this.isBulkResizeSelected
        ? 'Bulk Resize Selected Templates'
        : (this.isBulkResize ? 'Bulk Resize Templates' : 'Bulk Copy and Resize Template');
      this.$bvModal.show(MODAL_ID);
      this.canvasSize.width = this.selectedInstance.canvas.width;
      this.canvasSize.height = this.selectedInstance.canvas.height;
    },
    getTypeDimension(width, height) {
      if (this.isProportionalDimension) {
        return TYPE_DIMENSION.PROPORTIONAL;
      }

      return getTypeDimension(width, height, this.canvasSize);
    },
    onClose() {
      this.$nextTick(() => {
        this.$bvModal.hide(MODAL_ID);
      });
    },
    getSelectedTemplateIds() {
      return $('#project_name').multipleSelect('getSelects');
    },
    onSave() {
      const selectedTemplateIds = this.getSelectedTemplateIds();

      if (this.isBulkResizeSelected && !selectedTemplateIds.length) {
        toastr.error('Please select at least one template');
        return;
      }

      this.isSaving = true;
      const width = this.customSize.width;
      const height = this.customSize.height;
      if (!width || !height) {
        return;
      }

      const size = {
        width,
        height,
        type: this.getTypeDimension(width, height),
      };

      this.selectedSizes.push(size);

      const eventName = this.isBulkResizeSelected
        ? COPY_AND_RESIZE.BULK_COPY_SELECTED
        : COPY_AND_RESIZE.BULK_COPY;

      copyAndResizeEventBroker.fire(eventName, {
        selectedSizes: this.selectedSizes,
        alignContent: this.alignContent,
        deleteOriginalTemplates: this.isBulkResizeSelected ? true : this.isBulkResize,
        customerId: this.customerId,
        layoutId: this.layoutId,
        selectedTemplateIds: selectedTemplateIds,
      });
    },
    onMaintainAspectRatioChange(value) {
      if (value) {
        this.customSize.width = this.canvasSize.width;
        this.customSize.height = this.canvasSize.height;
      }
    },
    onCustomSizeChange(value, inputType) {
      if (!this.maintainAspectRatio) return;
      
      const currentWidth = this.canvasSize.width;
      const currentHeight = this.canvasSize.height;

      const aspectRatio = currentWidth / currentHeight;

      let newWidth;
      let newHeight;

      if (inputType === CUSTOM_SIZE_INPUT_TYPES.WIDTH_INPUT) {
        newHeight = +value / aspectRatio;
        this.customSize.height = Math.round(newHeight);
        
      } else {
        newWidth = +value * aspectRatio;
        this.customSize.width = Math.round(newWidth);
        
      }
    },
  }
}
</script>

<style scoped lang="scss">
</style>
